<template>
  <div class="my-10 w-screen m-auto px-0">
    <div class="max-w-screen-xl m-auto px-4">
      <Heading
        :level="1"
        h-class="text-h5  leading-5 lg:text-h4"
        :title="eventGetters.getTitle(eventData)"
        class="kft-heading--left"
        data-test-id="event-title"
      />
      <div
        class="text-thin text-base my-2 md:line-clamp-2 md:hover:line-clamp-none"
        v-html="eventGetters.getSubtitle(eventData)"
      ></div>
      <div v-if="!isGiftcard" class="text-gray-800 inline space-x-2 text-sm">
        <NuxtLink to="#sectionPartner">
          <span class="text-gray-500 hover:text-primary transition-colors">
            {{ eventGetters.getSupplierName(eventData) }}
          </span>
        </NuxtLink>
        <NuxtLink to="#sectionReviews" class="d-flex align-middle">
          <span class="text-gray-500 hover:text-primary transition-colors">
            <IconStar class="fill-current inline w-[18px]" />
            {{ eventGetters.getReviewsAverage(eventData) }}
            {{
              '- ' +
              $t(
                'product.reviews.reviewAmountLabel',
                Number(eventGetters.getReviewsTotal(eventData))
              )
            }}
          </span>
        </NuxtLink>
      </div>
      <EventGallery
        class="my-5"
        :event="eventData"
        :is-giftcard="isGiftcard"
      ></EventGallery>

      <!-- BEGIN::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->
      <div
        v-if="!props.isPreview"
        class="flex flex-col lg:flex-row-reverse gap-4"
      >
        <EventDetailsSidebar />
        <div class="w-auto flex-1 flex-col gap-5 overflow-hidden">
          <NuxtLazyHydrate when-visible>
            <BoxIconIntroTable :event-details="eventData" />
          </NuxtLazyHydrate>
          <div class="w-full my-10">
            <Heading
              :level="2"
              :title="getTitleByCategory('about', eventData)"
              h-class="text-h4"
              class="kft-heading--left mt-8 mb-4"
            ></Heading>
            <div class="">
              <NuxtLazyHydrate when-visible>
                <StrapiContentWrapper
                  :rich-text="eventGetters.getDescription(eventData)"
                />
              </NuxtLazyHydrate>
            </div>
            <div
              v-if="eventGetters.getCourseProgress(eventData)"
              class="mt-8 mb-4"
            >
              <Heading
                :level="2"
                :title="getTitleByCategory('progress', eventData)"
                h-class="text-h4"
                class="kft-heading--left mt-12 mb-4"
              />
              <NuxtLazyHydrate when-visible>
                <StrapiContentWrapper
                  :rich-text="eventGetters.getCourseProgress(eventData)"
                />
              </NuxtLazyHydrate>
            </div>
            <div v-if="eventGetters.getCourseTarget(eventData)" class="my-8">
              <Heading
                :level="2"
                :title="getTitleByCategory('courseTarget', eventData)"
                h-class="text-h4"
                class="kft-heading--left mt-12 mb-4"
              />
              <NuxtLazyHydrate when-visible>
                <StrapiContentWrapper
                  :rich-text="eventGetters.getCourseTarget(eventData)"
                />
              </NuxtLazyHydrate>
            </div>
            <!-- BEGIN::Private Event Info -->
            <NuxtLazyHydrate when-visible>
              <LazyPrivateEventInfo
                v-if="
                  eventGetters.getIsTeamEvent(eventData) &&
                  eventGetters.getPrivateEventDescription(eventData) !== ''
                "
                :description="
                  eventGetters.getPrivateEventDescription(eventData)
                "
              />
            </NuxtLazyHydrate>
            <!-- END::Private Event Info -->

            <Heading
              :level="2"
              :title="$t('product.show.boxDetails.detailsToRemember')"
              h-class="text-h4"
              class="kft-heading--left mt-12 mb-4"
            />
            <div itemscope itemtype="https://schema.org/FAQPage">
              <div
                v-for="(ok, i) in computedEventData.importantDetails"
                :key="i"
                itemprop="mainEntity"
                itemscope
                itemtype="https://schema.org/Question"
              >
                <Heading
                  :level="3"
                  h-class="h6 !mt-4 !-mb-4"
                  :title="ok.title"
                  class="kft-heading--left"
                  itemprop="name"
                />
                <div
                  itemprop="acceptedAnswer"
                  itemscope
                  itemtype="https://schema.org/Answer"
                >
                  <NuxtLazyHydrate when-visible>
                    <StrapiContentWrapper
                      itemprop="text"
                      class="w-full mt-5"
                      :rich-text="ok.description"
                    />
                  </NuxtLazyHydrate>
                </div>
              </div>
            </div>
          </div>
          <EventAddOnsDetails
            v-if="addOns && addOns.length > 0"
            :add-directly-to-cart="true"
            :add-ons="addOns"
            @click:addon="handleAddOnClick"
          />
          <!-- Begin::Ticket suggestion cards -->

          <!-- Remove ticket card showcase temporarily, including header
        <div v-if="!isGiftcard" class="my-10">
          <Heading
            :level="2"
            h-class="text-h3 font-semibold"
            :title="$t('product.ticketCardsSection.title')"
            class="mb-7 kft-heading--left"
          />
          <TicketCardShowcase
            :dates="[]"
            @click:ticket-card-cta="handleTicketCardClick"
          >
          </TicketCardShowcase>

        </div>
        -->
          <hr class="my-10" />
          <!-- END::Ticket suggestion cards -->
          <div v-if="!isGiftcard" id="sectionPartner">
            <!-- {{ eventGetters.getSupplier(eventData) }} -->
            <NuxtLink
              :to="
                localePath(`/p/${eventGetters.getSupplierPermalink(eventData)}`)
              "
            >
              <NuxtLazyHydrate when-visible>
                <UserProfileHeader
                  :title-level="2"
                  title-h-class="text-h5"
                  :subtitle="eventGetters.getSupplierUserName(eventData)"
                  :name="eventGetters.getSupplierName(eventData)"
                  :image-url="eventGetters.getSupplierLogo(eventData)"
                />
              </NuxtLazyHydrate>
            </NuxtLink>

            <div
              class="text-sm line-clamp-3 mb-6"
              v-html="eventGetters.getSupplierShortDescription(eventData)"
            ></div>
          </div>
        </div>
      </div>
      <!-- END::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->
    </div>

    <div
      v-if="eventGetters.getReviewsListRecentFirst(eventData).length > 0"
      class="!max-w-none py-5 md:py-10 bg-[#F9F7F5]"
    >
      <NuxtLazyHydrate when-visible>
        <LazyReviewShowcase
          id="sectionReviews"
          :average-rating="eventGetters.getReviewsAverage(eventData)"
          :review-count="eventGetters.getReviewsTotal(eventData)"
          :supplier-name="eventGetters.getSupplierName(eventData)"
          :reviews="eventGetters.getReviewsListRecentFirst(eventData)"
          :supplier-created-at="eventGetters.getSupplierCreatedAt(eventData)"
        />
      </NuxtLazyHydrate>
    </div>
    <div class="max-w-screen-xl m-auto px-4">
      <!-- Begin::Supplier events -->
      <div class="product-show-cases [&>div]:my-10">
        <NuxtLazyHydrate when-visible>
          <LazyProductShowcase
            v-if="parsedSupplierEvents && parsedSupplierEvents.length > 0"
            data-test-id="supplier-events"
            :products="parsedSupplierEvents"
            :tags="[]"
            :gtm-item-list-id="GtmItemListId.EventPageSameSupplierClasses"
            :title="
              isGiftcard
                ? $t('product.giftcardEventsFromTheSameSupplier')
                : $t('product.eventsFromTheSameSupplier')
            "
            :title-level="2"
            :use-h-tag="true"
            :algolia-insights-client="algoliaInsightsClient"
            algolia-insights-label="Supplier Events - Kit Page"
        /></NuxtLazyHydrate>
        <!-- End::Supplier events -->

        <!-- Begin::Similar Box Events -->
        <div v-if="!isSubdomain">
          <NuxtLazyHydrate when-visible>
            <ProductBoxShowcase
              :title="$t('product.boxSimilarLabel')"
              :title-level="2"
              :use-h-tag="true"
              class="my-10"
              :category="eventGetters.getMainCategorySlug(eventData)"
              :is-duration-visible="false"
              is-mobile-slider
            />
          </NuxtLazyHydrate>
        </div>
        <!-- End::Similar Box Events -->

        <NuxtLazyHydrate when-visible>
          <!-- Begin::Similar Online events -->
          <ProductShowcase
            v-if="
              !isGiftcard &&
              !isSubdomain &&
              similarOnlineEvents &&
              similarOnlineEvents.length > 0
            "
            :gtm-item-list-id="GtmItemListId.EventPageSimilarOnlineClasses"
            class="my-20"
            data-test-id="online-events"
            :products="similarOnlineEvents"
            :tags="[]"
            :title="$t('product.similarOnlineEvents')"
            :title-level="2"
            :use-h-tag="true"
            :algolia-insights-client="algoliaInsightsClient"
            algolia-insights-label="Similar Online Events - Kit Page"
          />
        </NuxtLazyHydrate>
      </div>
      <!-- :gtm-item-list-id="GtmItemListId.EventPageSimilarOnlineClasses" -->
      <!-- End::Similar Online events -->
      <ClientOnly v-if="!isSubdomain" when-visible>
        <div
          class="kft-product__summary__tags mt-6 mb-8 ms-0 gap-2 flex flex-wrap"
        >
          <NuxtLink
            v-for="(tag, i) in eventGetters.getTags(eventData)"
            :key="`kft-product__breadcrumbs__tags_chips-${i}`"
            :to="getChipLink(tag)"
          >
            <span
              class="bg-gray-100 text-sm me-2 px-2.5 py-0.5 rounded-full hover:shadow"
            >
              {{ tag.name }}
            </span>
          </NuxtLink>
        </div>
      </ClientOnly>

      <NuxtLazyHydrate when-visible>
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as qs from 'qs'
import IconStar from '~/components/icons/IconStar'
import type { EventDate, EventDescription } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import useSeoHelper from '~/composables/useSeoHelper'
import useKitProductSchema from '~/composables/schemaOrg/useKitProductSchema'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { sendProductDetailView } from '~/composables/useGtmEvents'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import { useEventRedirects } from '~/composables/useEvent'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { useTitleByCategory } from '~/composables/product/useGetTitleByCategory'
import { setLastViewedEvent } from '~/utils/lastViewedEvents'

interface Props {
  eventPermalink: string
  isGiftcard?: boolean
  isSpecialGiftcard?: boolean
  shouldAddEventSchema?: boolean
  isPreview?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  eventPermalink: undefined,
  isGiftcard: false,
  isSpecialGiftcard: false,
  shouldAddEventSchema: false,
  isPreview: false,
})
const isSubdomain = useIsSubdomain()
const route = useRoute()

const { t: $t } = useI18n()
const $localePath = useLocalePath()
const localePath = $localePath
const { query: queryParams } = useRoute()
const { cart, addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState()
const { getTitleByCategory } = useTitleByCategory()
const { sendAddToCartEvent } = useAlgoliaConversion()
const { client: algoliaInsightsClient } = useAlgoliaSearchInsights()
const buyBoxContainer = ref()
const { data: eventDataResponse, error: eventDataLoadError } =
  await useKftApiFetch<KftApiDefaultResponse<EventDescription>>(
    `v1/store/events/${props.eventPermalink}${
      props.isPreview ? `/preview` : ``
    }?${qs.stringify(
      {
        include: [
          'address.locality',
          'supplier',
          'supplier.logo',
          'reviews',
          'activePromotion',
        ],
      },
      { arrayFormat: 'comma' }
    )}`,
    {
      transform: (res) => {
        delete res?.data?.supplier?.data?.description
        return res
      },
    }
  )

const eventData = useEventData()
eventData.value = eventDataResponse.value?.data

// handle event redirects
await useEventRedirects(
  eventData?.value,
  eventDataLoadError?.value,
  props.isGiftcard,
  props.eventPermalink
)

const breadcrumbs = computed(() => {
  return [
    {
      text: $t('general.home'),
      link: localePath({ name: 'index' }),
    },
    {
      text: $t('product.boxEventLabel'),
      link: localePath({ name: 'kits' }),
    },
    {
      text: eventGetters.getTitle(eventData.value),
      link: localePath({
        name: 'b-boxPermalink',
        params: { boxPermalink: eventGetters.getPermalink(eventData.value) },
      }),
    },
  ]
})

provide('eventBuyBoxVisibleElements', {
  calendar: false,
  requestButtons: true,
  giftcard: true,
})
provide('eventData', eventData.value)
provide('isSpecialGiftcardEvent', false)

/**  DATA FETCHING **/
const { data: partnerEvents = ref([]) } =
  eventGetters.getSupplierId(eventData.value) &&
  useFetchSupplierEvents(eventGetters.getSupplierId(eventData.value), null, {
    transform: transformToAlgoliaEvent,
    default: () => ref([]),
  })

// special Giftcards are also Online events, this condition covers both cases
// const { data: similarEvents = ref([]) } =
//   eventGetters.getAttendanceMode(eventData.value) !== 'ONLINE'
//     ? useFetchSimilarEvents(eventGetters.getPermalink(eventData.value), {
//         transform: transformToAlgoliaEvent,
//         default: () => [],
//       })
//     : { data: ref([]) }

const { data: similarOnlineEvents = [] } =
  eventGetters.getMainCategorySlug(eventData.value) && !props.isGiftcard
    ? useFetchSimilarOnlineEvents(
        eventGetters.getMainCategorySlug(eventData.value),
        {
          transform: transformToAlgoliaEvent,
          default: () => [],
        }
      )
    : { data: ref([]) }

const {
  add_event_to_cart_quantity: quantity,
  add_event_to_cart_date_id: dateId,
  date_type: paramDateType,
} = queryParams

const eventId = eventGetters.getId(eventData?.value)

const { data: addToCardDate, execute: executeFetchAddToCartDate } =
  await useKftApiFetch<KftApiDefaultResponse<EventDate[]>, EventDate>(
    `v1/store/events/${eventId}/dates?search=id:${dateId};${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}&include=children`,
    {
      server: false,
      immediate: false,
      transform: (res) => {
        return res?.data && res.data[0]
      },
    }
  )

const { data: addOnsResponse } = await useKftApiFetch(
  `v1/store/events/${eventId}/add-ons?include=media`
)
const addOns = computed(() => addOnsResponse.value?.data)

/** COMPUTED **/
const computedEventData = computed(() => ({
  importantDetails: [
    {
      title: getTitleByCategory('whatToBring', eventData.value),
      description: eventGetters.getWhatToBring(eventData.value),
    },
    {
      title: getTitleByCategory('whatIsIncluded', eventData.value),
      description: eventGetters.getWhatIsIncluded(eventData.value),
    },
  ],
}))

const parsedSupplierEvents = computed(() => {
  return transformToAlgoliaEvent(partnerEvents.value)
    .filter((e) => e.hashed_id !== eventData.value.id)
    .slice(0, 10)
})

const shouldTryToFetchAddToCartLinks = computed(
  () =>
    quantity &&
    dateId &&
    eventId &&
    !cart.value.items.find(
      (i) => i.id === dateId && i.quantity === Number(quantity)
    )
)

const handleAddToCartLinks = async () => {
  const qtyTickets = Math.min(
    Number(quantity),
    addToCardDate.value?.availableTicketsQuantity || 1
  )

  if (addToCardDate.value && addToCardDate.value?.dateType && eventData.value) {
    const { dateType } = addToCardDate.value
    const dateIsPrivate = dateType === 'PRIVATE' || paramDateType === 'PRIVATE'

    const pricingsResponse = await useFetchPricings(ref(eventData.value.id), {
      dateId: ref(addToCardDate.value.id),
      quantity: ref(qtyTickets),
      isPrivate: ref(dateIsPrivate),
    })

    if (!pricingsResponse) {
      return
    }

    await book(
      parseEventDateToCartItem(
        eventData.value,
        qtyTickets,
        pricingsResponse,
        addToCardDate as Ref<EventDate>,
        ref([]),
        dateIsPrivate
      )
    )
  }
}

const book = async (params) => {
  await addItem(params)
  toggleCartSidebar()
}

const localisedTeamEventSlugPrefix = $t('eventListPage.teamEventSlugPrefix')
const getChipLink = (tag) =>
  !tag || !tag.slug
    ? ''
    : tag.slug.startsWith(localisedTeamEventSlugPrefix)
      ? localePath(
          `/teamevent/${tag.slug.replace(`${localisedTeamEventSlugPrefix}-`, '')}`
        )
      : localePath(`/t/${tag.slug}`)

const { script: productSchemas } = useKitProductSchema([eventData.value])

const handleAddOnClick = async (addOn) => {
  const queryId = route.query.qid?.toString() || null
  const offsetHeight = buyBoxContainer?.value?.offsetTop ?? 0

  window.scrollTo({
    // subract 130 to account for navbar
    top: offsetHeight - 130,
    behavior: 'smooth',
  })
  const selectedDate = toRef(eventGetters.getNextInstance(eventData.value))
  const cartItem = parseEventDateToCartItem(
    eventData.value,
    1,
    undefined,
    selectedDate,
    toRef(addOn),
    false,
    queryId
  )

  if (cartItem) {
    await addItem(cartItem)
    toggleCartSidebar()
  }
  sendAddToCartEvent(eventData)
}

onMounted(async () => {
  if (shouldTryToFetchAddToCartLinks.value) {
    await executeFetchAddToCartDate()
    handleAddToCartLinks()
  }

  if (eventData.value) {
    sendProductDetailView(eventData.value)
  }

  setLastViewedEvent(eventData.value?.id)
})

const seoOptions = computed(() => ({
  head: {
    script: [productSchemas],
    title: eventGetters.getTitle(eventData.value),
  },
  shouldBeIndexed: !props.isPreview,

  meta: {
    title:
      eventGetters.getTitle(eventData.value) +
      ' | ' +
      $t('general.titles.base'),
    'og:title':
      eventGetters.getTitle(eventData.value) +
      ' | ' +
      $t('general.titles.base'),
    description: eventGetters.getMetaDescription(eventData.value),
    'og:description': eventGetters.getMetaDescription(eventData.value),
    ...(eventGetters.getOpenGraphImage(eventData.value) && {
      'og:image': eventGetters.getOpenGraphImage(eventData.value),
    }),
    'og:type': 'product',
    ...(eventGetters.getMinimumPriceAmount(eventData.value) && {
      'product:price:amount':
        Number(eventGetters.getMinimumPriceAmount(eventData.value)) / 100,
    }),
    'product:price:currency': eventGetters.getMinimumPriceCurrency(
      eventData.value
    ),
    'og:availability': 'instock',
  },
}))
useSeoHelper(seoOptions)
</script>
